import React, { useEffect, useState, useCallback } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { Row, Col, Button } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import supplierApis from "../../../api/master/supplier";
import Swal from "sweetalert2";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import materialsApis from "../../../api/master/materials";
import usersApis from "../../../api/master/users";
import GstinVerificationModal from "../../../components/GstinVerificationModal";
import FormDatePicker from "../../form-components/DatePicker";
import dayjs from "dayjs";
import { SequenceConfigModal, SequenceInput, useSequenceNumbering } from "../../../components/sequence";

interface SelectOptions {
  label: string;
  value: string;
}
const GeneralInformation: React.FC = (props: any) => {
  const methods = useForm();
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    getValues,
    clearErrors,
  } = useFormContext();

  const sequence = useSequenceNumbering({
    moduleName: "Supplier",
    setValue,
    getValues,
    persistModeToForm: true,
  });

  const materials = watch("materials");
  const businessType = watch("bussinessType");

  useEffect(() => {
    if (materials?.length > 0) {
      findCategoryByMaterialTypeId(materials);
    }
  }, [materials]);

  const [categories, setCategories] = useState<SelectOptions[]>([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState<
    SelectOptions[]
  >([]);
  const [disableCategory, setDisableCategory] = useState(true);

  const [gstinModal, setGstinModal] = useState(false);
  const [states, setStates] = useState<SelectOptions[]>([]);
  const [districtOptions, setDistrictOptions] = useState<SelectOptions[]>([]);
  const [countries, setCountries] = useState<SelectOptions[]>([]);
  const [previousBusinessType, setPreviousBusinessType] = useState<string>("");
  const [isSettingGstData, setIsSettingGstData] = useState(false);
  const [gstVerifiedViaModal, setGstVerifiedViaModal] = useState(false);

  const getCountries = async () => {
    try {
      const _countries = await usersApis.getCountrys();
      if (_countries.status) {
        if (_countries.data.length > 0) {
          const transformedData = _countries.data.map((item: any) => ({
            value: item.countryId.toString(),
            label: item.countryName,
          }));
          setCountries(transformedData);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const fetchStatesByCountry = async (countryId: string) => {
    try {
      const data = await usersApis.getStatesByCountry(countryId);
      const stateOptions = data?.data?.map((state: any) => ({
        value: state.stateId.toString(),
        label: state.stateName,
      }));
      if (stateOptions.length > 0) setStates(stateOptions);
    } catch (error) { }
  };
  const getDistricts = async (stateID: any) => {
    try {
      const districts = await usersApis.getDistrictsByState(stateID);
      console.log(districts);
      if (districts.status) {
        if (districts.data.length > 0) {
          const transformedData = districts.data.map((item: any) => ({
            value: item.districtId.toString(),
            label: item.districtName,
          }));
          setDistrictOptions(transformedData);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const fetchIndustryType = async () => {
    try {
      const { data, status, message } =
        await supplierApis.fetchMaterialTyepesByCategory();
      if (status) {
        setIndustryTypeOptions(
          data.map((obj: any) => ({ label: obj.name, value: obj.material_id }))
        );
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
  };

  const countryWatch = watch("country");
  const stateWatch = watch("state");
  useEffect(() => {
    if (countryWatch) {
      fetchStatesByCountry(countryWatch);
    }
  }, [countryWatch]);
  useEffect(() => {
    if (stateWatch) {
      getDistricts(stateWatch);
    }
  }, [stateWatch]);

  useEffect(() => {
    fetchIndustryType();
    getCountries();
    fetchStatesByCountry("1");
    sequence.fetchSequenceAccess();
  }, []);

  const findCategoryByMaterialTypeId = async (materialTypeId: any) => {
    const idsString = materialTypeId && materialTypeId.join(",");
    try {
      const { data, status, message } =
        await materialsApis.findCategoryByMaterialType(idsString);
      if (status) {
        setCategories(
          data.map((obj: any) => ({ label: obj.category, value: obj.id }))
        );
        setDisableCategory(false);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
  };

  useEffect(() => {
    if (previousBusinessType && previousBusinessType !== businessType) {
      // Only reset GST-related fields, not all fields
      resetGstRelatedFields();
      setGstVerifiedViaModal(false);
    }
    setPreviousBusinessType(businessType);
  }, [businessType]);

  const resetGstRelatedFields = () => {
    clearErrors();
    // Only reset GST-specific fields
    const gstFieldsToReset = [
      "gstin",
      "cin",
      "incorporationDate",
      "pan",
      "companyName",
      "country",
      "state",
      "district",
      "city",
      "area",
      "mailingAddressLine1",
      "pincode",
      "lastFYTurnover",
      "applicationAccess",
    ];

    gstFieldsToReset.forEach((field) => {
      setValue(field, "", { shouldValidate: false });
    });
  };

  const isGstVerified = () => {
    const values = getValues();
    const panValid = !!values.pan && /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(values.pan);
    return !!values.gstin && panValid;
  };

  useEffect(() => {
    if (businessType === "registered" && !isGstVerified()) {
      setGstinModal(true);
    } else {
      setGstinModal(false);
    }
  }, [businessType]);

  const findOptionValue = (label: string, options: SelectOptions[]) => {
    const found = options.find((option) => option.label === label);
    return found ? found.value : "";
  };

  const handleGSTVerification = async (gstData: any) => {
    console.log("Verified GST Data:", gstData);

    setIsSettingGstData(true);
    setGstVerifiedViaModal(true);
    clearErrors();

    // Improved district matching logic
    let districtValue = "";
    if (gstData.district) {
      const districtByName = districtOptions.find(
        (option) =>
          option.label.toLowerCase() === gstData.district.toLowerCase()
      );
      const districtByValue = districtOptions.find(
        (option) =>
          option.value.toLowerCase() === gstData.district.toLowerCase()
      );
      districtValue = districtByName?.value || districtByValue?.value || "";
    }

    // Format the incorporation date if it exists
    let formattedIncorporationDate: any = null;
    if (gstData.incorporationDate) {
      const parsed = dayjs(gstData.incorporationDate);
      if (parsed.isValid()) {
        formattedIncorporationDate = parsed;
      }
    }

    setValue("incorporationDate", formattedIncorporationDate, {
      shouldValidate: false,
    });

    // Set all values first without validation
    const updates = [
      () => setValue("gstin", gstData.gstin || "", { shouldValidate: false }),
      () => setValue("pan", gstData.pan || "", { shouldValidate: false }),
      () =>
        setValue("supplierName", gstData.LegalName || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("tradeName", gstData.TradeName || gstData.LegalName || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("country", findOptionValue(gstData.country, countries) || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("state", findOptionValue(gstData.state, states) || "", {
          shouldValidate: false,
        }),
      () => setValue("district", districtValue, { shouldValidate: false }),

      () => setValue("city", gstData.city || "", { shouldValidate: false }),
      () => setValue("area", gstData.area || "", { shouldValidate: false }),
      () =>
        setValue("mailingAddressLine1", gstData.addressLine1 || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("pincode", gstData.zipPincode || "", {
          shouldValidate: false,
        }),
    ];

    // Execute all setValue calls
    updates.forEach((update) => update());

    // Wait for React to process the state updates
    setTimeout(() => {
      // Trigger validation for all relevant fields
      const fieldsToValidate = [
        "gstin",
        "pan",
        "supplierName",
        "tradeName",
        "country",
        "state",
        "district",
        "city",
        "area",
        "mailingAddressLine1",
        "pincode",
      ];

      trigger(fieldsToValidate).then((isValid) => {
        console.log("Validation result after GST data:", isValid);
        setIsSettingGstData(false);
      });
    }, 300);
  };

  return (
    <>
      {/* <FormProvider {...methods}> */}
      <form
        className="content clearfix"
        onSubmit={handleSubmit(() => {
          props.next();
        })}
      >
        <h5 className="mb-2">Legal Information</h5>
        <Row className="gy-4">
          <Col lg="4">
            <FormSelect
              name="bussinessType"
              label="Business Type"
              required={true}
              options={[
                { label: "Registered", value: "registered" },
                { label: "Unregistered", value: "unregistered" },
              ]}
              placeholder="Select Business Type"
            />
          </Col>
          {/* Conditional Fields */}
          {businessType === "registered" && (
            <>
              <Col lg="4">
                <FormInput
                  name="gstin"
                  label="GSTIN"
                  required
                  type="text"
                  pattern={
                    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/
                  }
                  placeholder="Auto-filled GSTIN"
                  disabled={gstVerifiedViaModal}
                />
              </Col>

              <Col lg="4">
                <FormInput
                  name="cin"
                  label="CIN"
                  type="text"
                // placeholder="Auto-filled CIN"
                // disabled
                />
              </Col>

              <Col lg="4">
                <FormDatePicker
                  name="incorporationDate"
                  label="Incorporation Date"
                />
              </Col>

              <Col lg="4">
                <FormInput
                  name="lastFYTurnover"
                  label="Last FY Turn Over (INR)"
                  type="text"
                  placeholder="Enter Last FY Turnover"
                  pattern={/^\d+$/}
                />
              </Col>
            </>
          )}
          {(businessType === "registered" ||
            businessType === "unregistered") && (
              <Col lg="4">
                <FormInput
                  name="pan"
                  label="PAN"
                  required
                  type="text"
                  maxLength={10}
                  pattern={/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/}
                  onInput={(e: any) => {
                    e.target.value = e.target.value
                      .replace(/[^a-zA-Z0-9]/g, "")
                      .toUpperCase();
                  }}
                  placeholder={
                    businessType === "registered"
                      ? "Auto-filled PAN"
                      : "Enter PAN"
                  }
                  disabled={businessType === "registered" && gstVerifiedViaModal}
                />
              </Col>
            )}

          {businessType === "unregistered" && (
            <Col lg="4">
              <FormInput
                name="cin"
                label="CIN"
                type="text"
                placeholder="Enter CIN"
              />
            </Col>
          )}
        </Row>

        <h5 className="mt-4 mb-2">General Information</h5>
        <Row className="gy-4">
          <Col lg="4">
            <SequenceInput
              label="Supplier ID"
              numberingMode={sequence.numberingMode}
              onSettingsClick={sequence.openModal}
            />
          </Col>

          <Col lg="4">
            <FormInput
              name="supplierName"
              label="Supplier Name(Legal Name)"
              disabled={businessType === "registered" && gstVerifiedViaModal}
              // disabled={businessType === "registered"}
              required={true}
              pattern={/^[A-Za-z\s!@#$%^&*(),.?":{}|<>/&.-]+$/}
              type="text"
              // maxLength={50}
              placeholder="Enter Supplier Name"
              onInput={(e: any) => {
                e.target.value = e.target.value.replace(/[^A-Za-z\s.&/-]/g, ""); // Removes invalid characters
              }}
            />
          </Col>
          <Col lg="4">
            <FormMultiSelect
              name="materials"
              label="Material Type"
              options={industryTypeOptions}
              required={true}
              placeholder="Select Material Type"
              onBlur={(event, values) => {
                setValue("categories", []);
              }}
            />
          </Col>
        </Row>
        <Row className="gy-4 mt-1">
          <Col lg="4">
            <FormMultiSelect
              name="categories"
              label="Supply Categories"
              required={true}
              options={categories}
              disabled={disableCategory}
              placeholder="Select Supply Categories"
            />
          </Col>
          <Col lg="4">
            <FormInput
              name="tradeName"
              label="Trade Name"
              required={true}
              type="text"
              placeholder="Enter Trade Name"
              pattern={/^[A-Za-z\s!@#$%^&*(),.?":{}|<>/&.-]+$/}
              onInput={(e: any) => {
                e.target.value = e.target.value.replace(/[^A-Za-z\s.&/-]/g, ""); // Removes invalid characters
              }}
              disabled={businessType === "registered" && gstVerifiedViaModal}
            />
          </Col>
          <Col lg="4">
            <FormInput
              name="brandName"
              label="Brand Name"
              required={false}
              type="text"
              placeholder="Enter Brand Name"
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="deliveryLeadDays"
              label="Delivery Lead Days"
              required={true}
              options={[
                { label: "Immediate", value: "Immediate" },
                { label: "<1 Week", value: "<1 Week" },
                { label: ">1 Week", value: ">1 Week" },
              ]}
              placeholder="Select Delivery Lead Days"
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="paymentTerms"
              label="Payment Terms"
              required={true}
              options={[
                { label: "Advance", value: "Advance" },
                { label: "Credit", value: "Credit" },
              ]}
              placeholder="Enter Payment Terms"
            />
          </Col>
          {watch("paymentTerms") == "Credit" && (
            <Col lg="4">
              <FormInput
                name="creditDays"
                label="Credit Days"
                required={true}
                type="number"
                placeholder="Enter Credit Days"
              />
            </Col>
          )}
        </Row>
        <Row className="gy-4 mt-1">
          <Col>
            <Button color="primary" type="submit">
              Next
            </Button>
          </Col>
        </Row>
      </form>
      {/* </FormProvider> */}

      {/* GstinVerificationModal usage: */}
      <GstinVerificationModal
        isOpen={gstinModal}
        toggle={() => {
          setGstinModal(!gstinModal);
        }}
        onVerified={handleGSTVerification}
        countries={countries}
        states={states}
        districts={districtOptions}
        source="suppliers"
      />

      <SequenceConfigModal
        moduleName="Supplier"
        fieldLabel="Supplier ID"
        show={sequence.showNumberingModal}
        tempNumberingMode={sequence.tempNumberingMode}
        setTempNumberingMode={sequence.setTempNumberingMode}
        canAuto={sequence.canAuto}
        currentSequence={sequence.currentSequence}
        activeSequences={sequence.activeSequences}
        onSave={sequence.saveModalSelection}
        onClose={sequence.closeModal}
      />
    </>
  );
};

export default React.memo(GeneralInformation);
